import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import toast from "components/partials/toast/toast";

import { getSmsSimulatorRule } from "generated/api/sms-simulator-rule";
import { SmsSimulatorRuleDTO, SmsSimulatorRuleRequest } from "generated/schemas";

import { smsSimulatorRuleKeys } from "hooks/api/query-keys";

import { SmsSimulatorRuleAttributes } from "containers/sms-simulator/sms-simulator.types";

const rulesApi = getSmsSimulatorRule();

const mapRule = (dto: SmsSimulatorRuleDTO): SmsSimulatorRuleAttributes => ({
  id: dto.id ?? "",
  description: dto.description ?? "",
  ruleOrder: dto.ruleOrder ?? 0,
});

const sortByOrder = (rules: SmsSimulatorRuleAttributes[]): SmsSimulatorRuleAttributes[] =>
  [...rules].sort((a, b) => a.ruleOrder - b.ruleOrder);

export const useSmsSimulatorRules = <TData = SmsSimulatorRuleAttributes[]>(
  options?: Omit<
    UseQueryOptions<SmsSimulatorRuleAttributes[], Error, TData>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: smsSimulatorRuleKeys.list(),
    queryFn: async () => {
      const data = await rulesApi.getAll();
      return sortByOrder(data.map(mapRule));
    },
    ...options,
  });

export const useCreateSmsSimulatorRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SmsSimulatorRuleRequest) => rulesApi.create(request),
    onSuccess: () => {
      toast.success({ data: { title: "Created rule" } });
      queryClient.invalidateQueries({ queryKey: smsSimulatorRuleKeys.lists() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to save rule" } });
    },
  });
};

export const useUpdateSmsSimulatorRuleDescription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: SmsSimulatorRuleRequest }) =>
      rulesApi.updateDescription({ id }, request),
    onSuccess: () => {
      toast.success({ data: { title: "Updated description" } });
      queryClient.invalidateQueries({ queryKey: smsSimulatorRuleKeys.lists() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to save rule" } });
    },
  });
};

/**
 * Reorders rules by PUT-ing the full list (backend assigns ruleOrder by position).
 * Optimistically updates the cache so the drag interaction feels instant, and
 * rolls back on error.
 */
export const useReorderSmsSimulatorRules = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedRules: SmsSimulatorRuleAttributes[]) =>
      rulesApi.updateAll(orderedRules.map((rule) => ({ description: rule.description }))),
    onMutate: async (orderedRules) => {
      await queryClient.cancelQueries({ queryKey: smsSimulatorRuleKeys.lists() });

      const previous = queryClient.getQueryData<SmsSimulatorRuleAttributes[]>(
        smsSimulatorRuleKeys.list(),
      );

      const reordered = orderedRules.map((rule, index) => ({ ...rule, ruleOrder: index + 1 }));
      queryClient.setQueryData(smsSimulatorRuleKeys.list(), reordered);

      return { previous };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(smsSimulatorRuleKeys.list(), sortByOrder(data.map(mapRule)));
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(smsSimulatorRuleKeys.list(), context.previous);
      }
      toast.error({ data: { title: "Failed to reorder rules" } });
    },
  });
};

export const useDeleteSmsSimulatorRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => rulesApi.delete2({ id }),
    onSuccess: () => {
      toast.success({ data: { title: "Deleted rule" } });
      queryClient.invalidateQueries({ queryKey: smsSimulatorRuleKeys.lists() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to delete rule" } });
    },
  });
};
